import React from 'react';
import { Button, Input } from '@/shared/components/ui';
import { getRequiredLabel } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '@/shared/assets/calendar-icon.svg?react';
import { DEFAULT_DATE_PICKER_INPUT_YEAR } from '@/shared/config';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  handleDateModalOpen: () => void;
}

const DatePickerInput = React.forwardRef<HTMLInputElement, Props>(
  ({ handleDateModalOpen, onChange, ...props }, ref) => {
    const { t } = useTranslation();

    function formatDate(value: string) {
      const digits = value.replace(/\D/g, '').slice(0, 8);

      const day = digits.slice(0, 2);
      const month = digits.slice(2, 4);
      const year = digits.slice(4, 8);

      let result = day;

      if (month) result += `.${month}`;
      if (year) {
        if (year.length === 4) {
          const numericYear = Number(year);

          const safeYear =
            numericYear < DEFAULT_DATE_PICKER_INPUT_YEAR
              ? DEFAULT_DATE_PICKER_INPUT_YEAR
              : numericYear;

          result += `.${safeYear}`;
        } else {
          result += `.${year}`;
        }
      }

      return result;
    }

    const handleChange = React.useCallback(
      (newValue: string) => {
        const syntheticEvent = {
          target: { value: newValue },
          currentTarget: { value: newValue },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange?.(syntheticEvent);
      },
      [onChange]
    );

    return (
      <Input
        {...props}
        onBeforeInput={e => {
          if (!/\d/.test(e.data || '')) {
            e.preventDefault();
          }
        }}
        ref={ref}
        type="text"
        inputMode="numeric"
        maxLength={10}
        pattern="\d{2}\.\d{2}\.\d{4}"
        placeholder={t(
          'personalAccount.verification.eulaProfileForm.dateOfBirthPlaceholder'
        )}
        label={getRequiredLabel(
          t('personalAccount.verification.eulaProfileForm.dateOfBirthLabel')
        )}
        className="p3"
        onChange={e => handleChange(formatDate(e.target.value))}
        rightContent={
          <Button
            disabled={props.disabled}
            type="button"
            size="icon-button"
            variant="icon"
            onClick={handleDateModalOpen}>
            <CalendarIcon className="size-[2.4rem]" />
          </Button>
        }
      />
    );
  }
);

DatePickerInput.displayName = 'DatePickerInput';

export { DatePickerInput };
